import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import {
  SignInType,
  type SessionMetadata,
  type SessionMetadataResponse,
} from '@bbkAdminRedux/rtkq/auth-types';
import {
  type BbkClientConfig,
  type CbIdpRegion,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import {
  encodeObject,
  encodeObjectForQuery,
} from '@bbkAdminRedux/rtkq/rtkq-utils';

export type ChargebeeSignInArgs = {
  email?: string;
  jwt?: string;
  cbLoginBaseUrl?: string;
};

export type ChargebeeGetSignInUrlArgs = {
  email?: string;
  redirect?: string;
  cbLoginBaseUrl?: string;
  region?: CbIdpRegion;
};

export class AuthChargebee {
  private config: BbkClientConfig;

  private static BASE_URI = window.location.origin;

  private static LOGIN_REDIRECT_URL = `${AuthChargebee.BASE_URI}${LogInPaths.cbidpExchange}`;

  private static exchangeTokenURL = `/api/v1/cbidp/exchange_token`;

  constructor(config: BbkClientConfig) {
    this.config = config;
  }

  public getSignInUrl({
    email,
    redirect,
    cbLoginBaseUrl,
    region,
  }: ChargebeeGetSignInUrlArgs) {
    const query = encodeObject({
      client_id: this.config.cbidp_client_id,
      forward: `${AuthChargebee.LOGIN_REDIRECT_URL}${encodeObjectForQuery({
        redirect,
        originalEmail: email,
      })}`,
      login_hint: email,
    });
    return `${cbLoginBaseUrl || (region && this.config.cbidp_base_uri[region]) || this.config.cbidp_base_uri.us}/login?${query}`;
  }

  /**
   * Exchange the initial Chargebee IDP {@code authorization_code} for a
   * session. The backend performs the token exchange, sets the HttpOnly session
   * cookie, and returns only non-secret metadata — the access JWT is never sent
   * to the browser. Only the initial authorization code is accepted here;
   * keeping the session alive afterwards is the backend's own business, done on
   * the requests the app is already making.
   */
  private exchangeAuthorizationCode = (jwt: string): Promise<SessionMetadata> =>
    fetch(AuthChargebee.exchangeTokenURL, {
      method: 'POST',
      body: JSON.stringify({ jwt }),
    }).then(async (response) => {
      const result = await response.json();
      if (!response.ok) {
        // Preserve the HTTP status alongside the body so callers can distinguish
        // a terminal 401/403 from a transient 429/5xx.
        throw Object.assign(
          new Error(`Chargebee token exchange failed (${response.status})`),
          { status: response.status, data: result }
        );
      }
      const metadata = result as SessionMetadataResponse;
      return {
        type: SignInType.CHARGEBEE,
        email: metadata.email,
      };
    });

  public signIn = async ({
    email,
    jwt,
    cbLoginBaseUrl,
  }: ChargebeeSignInArgs): Promise<SessionMetadata> => {
    if (jwt) return this.exchangeAuthorizationCode(jwt);

    const loginURL = this.getSignInUrl({
      email,
      cbLoginBaseUrl,
      redirect: window.location.pathname + window.location.search,
    });

    window.location.assign(loginURL);
    // FIXME: It's a hack, because window.location.assign redirects to different url
    // I return it just to comply with rtkq contract
    return undefined!;
  };
}
